import { useCurrentuser } from '@uplift/contexts';
import maxBy from 'lodash/maxBy';
import { Matches } from '../components';
import { CareType, MatchPreference } from '@uplift/api';
import { useConfirmMatchingMutation, useUserInsurancesQuery, useUserMatchesQuery, useUserProfileQuery } from '@uplift/queries';
import { MatchesSelectProviderData, MatchesUpdateFiltersData, MatchScheduleSessionData } from '../onboarding/default/types';
import { Redirect, useHistory } from 'react-router';
import { AsyncButton, Button, ContextualNavigationLayout, UpliftThemeProvider } from '@uplift/ui-v2';
import { useState } from 'react';
import { Match } from '../onboarding/default/steps/match';
import { ArrowBack } from '@mui/icons-material';
import { Loading } from '@uplift/pages';
import { ProviderMedicalRole } from '@uplift/types';
import { SELF_PAY_ID } from '../onboarding/utils';

/**
 * Setup only for therapy matching
 */

type MatchingProps = {
  matchPreference: MatchPreference;
  patientLocationStateId?: string;
  insuranceId?: string;
};

const Matching: React.VFC<MatchingProps> = ({ matchPreference, patientLocationStateId, insuranceId }) => {
  const me = useCurrentuser();
  const history = useHistory();
  const matchLocationState = patientLocationStateId ?? matchPreference.locationState;
  const [ selectedProvider, setSelectedProvider ] = useState<MatchesSelectProviderData | null>(null);
  const confirmMatchingMutation = useConfirmMatchingMutation(me.id);
  const [ matchingFiltersData, setMatchingFiltersData ] = useState<MatchesUpdateFiltersData>({
    specialtyIds: matchPreference.visitReasonIds,
    insuranceId: insuranceId,
    identityFilters: {
      genderIds: matchPreference.genderIds,
      faithIds: matchPreference.faithIds,
      ethnicityIds: matchPreference.ethnicityIds,
    },
    experienceFilters: {
      faithIds: matchPreference.communityExpertiseFaithIds,
      ethnicityIds: matchPreference.communityExpertiseEthnicityIds,
      communityIds: matchPreference.communityExpertiseGeneralIds,
    },
    availabilityTimeOfDay: [],
    availabilityDayOfWeek: [],
  });

  const onScheduleSession = async (data: MatchScheduleSessionData) => {
    await confirmMatchingMutation.mutateAsync({
      timeStart: data.appointment.fromDatetime,
      providerId: selectedProvider?.providerId,

      matchPreferences: {
        stateId: matchLocationState,
        modalityId: matchPreference.modalityId,
        ageGroupId: matchPreference.ageGroupId,
        appointmentType: CareType.therapy,

        insuranceCompanyId: matchingFiltersData.insuranceId === SELF_PAY_ID ? null : insuranceId,
        visitReasonIds: matchingFiltersData.specialtyIds,

        faithIds: matchingFiltersData.identityFilters.faithIds,
        ethnicityIds: matchingFiltersData.identityFilters.ethnicityIds,
        genderIds: matchingFiltersData.identityFilters.genderIds,
        communityExpertise: {
          faithIds: matchingFiltersData.experienceFilters.faithIds,
          ethnicityIds: matchingFiltersData.experienceFilters.ethnicityIds,
          generalIds: matchingFiltersData.experienceFilters.communityIds,
        },
      }
    });
  };

  const onExit = () => {
    history.goBack();
  };

  const onUpdateFilters = async (data: MatchesUpdateFiltersData) => {
    setMatchingFiltersData(prev => ({
      ...prev,
      ...data,
    }));
  };

  if (selectedProvider) {
    return (
      <ContextualNavigationLayout
        headerProps={{
          rightButton: <AsyncButton onClick={onExit}>Exit</AsyncButton>,
          leftButton: (
            <Button
              startIcon={<ArrowBack />}
              onClick={() => setSelectedProvider(null)}
            >
              Back
            </Button>
          ),
        }}
        fullWidth
      >
        <Match
          selectedProviderId={selectedProvider.providerId}
          insuranceId={insuranceId}
          specialtyIds={matchPreference.visitReasonIds}
          isSlotUnavailable={false}
          onScheduleSession={onScheduleSession}
          stateId={matchLocationState}
          matchingPaymentType={selectedProvider.matchingPaymentType}
        />
      </ContextualNavigationLayout>
    );
  }

  return (
    <ContextualNavigationLayout
      headerProps={{
        rightButton: <AsyncButton onClick={onExit}>Exit</AsyncButton>
      }}
      fullWidth
    >
      <Matches
        matchingData={{
          stateId: matchLocationState,
          ageId: matchPreference.ageGroupId,
          medicalRole: ProviderMedicalRole.therapist,
          modalityId: matchPreference.modalityId,
          seed: matchPreference.seed,

          ...matchingFiltersData,
        }}
        onSelectProvider={setSelectedProvider}
        onUpdateFilters={onUpdateFilters}
      />
    </ContextualNavigationLayout>
  );
};

export const MatchingPage: React.VFC = () => {
  const me = useCurrentuser();
  const { data: matches, isInitialLoading: matchesLoading } = useUserMatchesQuery(me.id);
  const { data: profile } = useUserProfileQuery(me.id);
  const { data: patientInsurances, isLoading: isPatientInsurancesLoading } = useUserInsurancesQuery(me.id, { cacheTime: 0 });
  const referenceMatch = maxBy(matches?.allMatches, e => e?.match?.createdAt);
  const isLoading = matchesLoading || isPatientInsurancesLoading;
  const primaryInsurance = patientInsurances?.find((insurance) => insurance.insurance.isSecondaryInsurance === false);

  if (isLoading) {
    return (
      <UpliftThemeProvider>
        <Loading />
      </UpliftThemeProvider>
    );
  }

  const hasActiveTherapistMatch = matches.activeMatches?.find(x => x.provider.medicalRole === ProviderMedicalRole.therapist);

  if (hasActiveTherapistMatch) {
    return <Redirect to="/main" />;
  }

  return (
    <Matching
      matchPreference={referenceMatch?.matchPreferences}
      patientLocationStateId={profile?.address?.stateId}
      insuranceId={primaryInsurance?.insuranceCompany?.id ?? referenceMatch?.matchPreferences?.insuranceMatch}
    />
  );
};
